import { useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Server, Crown } from "lucide-react";

const Card = ({ title, children, testid }) => (
  <div className="border border-white/10 bg-[#0D0E15] p-4" data-testid={testid}>
    <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase mb-2">{title}</div>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 text-xs py-0.5">
    <span className="text-[#64748B]">{k}</span>
    <span className="text-white font-mono text-right truncate">{v ?? "—"}</span>
  </div>
);

const yn = (b) => (b ? "ON" : "off");
const VLEVEL = ["None", "Low", "Medium", "High", "Highest"];

const Header = ({ d }) => {
  const [iconErr, setIconErr] = useState(false);
  return (
  <div className="border border-white/10 bg-[#0D0E15] p-5 flex items-center gap-4" data-testid="guild-lookup-header">
    {d.icon && !iconErr ? <img src={`https://cdn.discordapp.com/icons/${d.guild_id}/${d.icon}.${d.icon.startsWith("a_") ? "gif" : "png"}?size=96`} onError={() => setIconErr(true)} className="w-14 h-14 rounded-xl" alt="" />
      : <div className="w-14 h-14 rounded-xl bg-[#06070A] flex items-center justify-center"><Server className="w-6 h-6 text-[#64748B]" /></div>}
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-lg truncate" data-testid="guild-lookup-name">{d.name || "Unknown server"}</span>
        {d.pro.active && <span className="flex items-center gap-1 px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] font-mono text-[10px]"><Crown className="w-3 h-3" />{d.pro.lifetime ? "PRO · LIFETIME" : "PRO"}</span>}
        <span data-testid="guild-lookup-bot-status" className={`px-2 py-0.5 font-mono text-[10px] ${d.bot_in_guild ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>{d.bot_in_guild ? "BOT IN SERVER" : "BOT LEFT"}</span>
      </div>
      <div className="font-mono text-xs text-[#64748B]">{d.guild_id} · created {new Date(d.created_at).toLocaleDateString()}</div>
      {d.description && <div className="text-xs text-[#94A3B8] mt-1 line-clamp-2">{d.description}</div>}
    </div>
  </div>
  );
};

const Details = ({ d }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <Card title="Owner" testid="guild-lookup-owner">
      {d.owner ? (
        <div className="flex items-center gap-3">
          {d.owner.avatar && <img src={`https://cdn.discordapp.com/avatars/${d.owner.id}/${d.owner.avatar}.png?size=64`} className="w-10 h-10 rounded-full" alt="" />}
          <div className="min-w-0">
            <div className="text-sm truncate">{d.owner.global_name || d.owner.username || "?"}</div>
            <div className="font-mono text-[10px] text-[#64748B]">{d.owner.username} · {d.owner.id}</div>
          </div>
        </div>
      ) : <div className="text-xs text-[#64748B]">Unavailable.</div>}
    </Card>
    <Card title="Size" testid="guild-lookup-counts">
      <Row k="Members" v={d.counts.members} /><Row k="Online" v={d.counts.online} />
      <Row k="Channels" v={`${d.counts.channels} (${d.counts.text_channels} text · ${d.counts.voice_channels} voice)`} />
      <Row k="Roles" v={d.counts.roles} /><Row k="Boosts" v={`${d.counts.boosts ?? 0} · tier ${d.boost_tier ?? 0}`} />
    </Card>
    <Card title="Security" testid="guild-lookup-security">
      <Row k="Health grade" v={d.security ? `${d.security.grade} · ${d.security.score}/${d.security.max_score}` : null} />
      <Row k="Discord verification" v={VLEVEL[d.verification_level] ?? null} />
      <Row k="2FA for mods" v={d.mfa_level ? "required" : "off"} />
      <Row k="Protection guards" v={`${d.config.protection_on}/${d.config.protection_total}`} />
    </Card>
    <Card title="ModNode setup" testid="guild-lookup-config">
      <Row k="AI moderation" v={d.config.ai_enabled ? d.config.ai_model : "off"} />
      <Row k="OAuth verify" v={yn(d.config.verify_enabled)} />
      <Row k="VPN block" v={yn(d.config.block_vpn)} /><Row k="Alt detection" v={yn(d.config.block_alts)} />
      <Row k="Audit channel" v={d.config.audit_channel_id} /><Row k="Vanity link" v={d.config.vanity_slug} />
    </Card>
    <Card title="Activity" testid="guild-lookup-activity">
      <Row k="Mod actions 7d / 30d" v={`${d.activity.actions_7d} / ${d.activity.actions_30d}`} />
      <Row k="Mod actions total" v={d.activity.actions_total} /><Row k="Warnings" v={d.activity.warnings} />
      <Row k="AI flags" v={d.activity.ai_flags} /><Row k="Verifications" v={d.activity.verifications} />
      <Row k="Blocked verifies" v={d.activity.verify_blocks} /><Row k="Support tickets" v={d.activity.support_tickets} />
    </Card>
    <Card title="ModNode+" testid="guild-lookup-pro">
      <Row k="Status" v={d.pro.active ? "active" : "free"} />
      <Row k="Lifetime" v={d.pro.active ? (d.pro.lifetime ? "yes" : "no") : null} />
      <Row k="Expires" v={d.pro.expires_at ? new Date(d.pro.expires_at).toLocaleDateString() : null} />
      <Row k="First seen" v={d.first_seen_at ? new Date(d.first_seen_at).toLocaleDateString() : null} />
    </Card>
  </div>
);

const Recent = ({ items }) => (
  <Card title={`Recent mod actions · ${items.length}`} testid="guild-lookup-recent">
    {items.length === 0 ? <div className="text-xs text-[#64748B]">No actions logged.</div> : items.map(a => (
      <div key={a.id} className="flex gap-3 text-xs py-0.5 border-b border-white/5">
        <span className="font-mono text-[#F59E0B] w-24 shrink-0 uppercase">{a.action}</span>
        <span className="text-[#94A3B8] truncate">{a.reason || a.target}</span>
        <span className="ml-auto font-mono text-[10px] text-[#64748B] whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
      </div>
    ))}
  </Card>
);

export const GuildLookup = () => {
  const [gid, setGid] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const search = async () => {
    const id = gid.trim();
    if (!/^\d{15,}$/.test(id)) return toast.error("Enter a valid Discord server ID (17-19 digits)");
    setLoading(true);
    try { const r = await api.get(`/owner/guild-lookup/${id}`); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed"); setData(null); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4" data-testid="guild-lookup">
      <div className="border border-white/10 bg-[#0D0E15] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Server className="w-4 h-4 text-[#8B5CF6]" />
          <span className="font-mono text-[10px] tracking-widest text-[#94A3B8] uppercase">Guild Lookup</span>
        </div>
        <div className="flex gap-2">
          <input data-testid="guild-lookup-input" value={gid} onChange={e => setGid(e.target.value)} onKeyDown={e => e.key === "Enter" && search()}
            placeholder="Paste a Discord server ID..."
            className="flex-1 bg-[#06070A] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#8B5CF6] font-mono rounded-md" />
          <button data-testid="guild-lookup-btn" onClick={search} disabled={loading} className="px-5 py-2 whitespace-nowrap bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-medium text-sm rounded-md disabled:opacity-50">
            {loading ? "..." : "Look up"}
          </button>
        </div>
      </div>
      {data && (<><Header d={data} /><Details d={data} /><Recent items={data.recent_actions || []} /></>)}
    </div>
  );
};
